import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import { RestStopGameLayout } from "Layouts/RestStopGameLayout";
import { AbstractGameState } from "./AbstractGameState";
import { FlyingGame } from "./FlyingGame";
import { TimelineLite, TimelineMax, TweenLite } from "gsap";
import { ResourceButton } from "../GameObjects/RestStopGame/ResourceButton";
import { StressorButton } from "../GameObjects/RestStopGame/StressorButton";
import GameState, { IDuckState, IRuntimeDuck } from "Data/GameState";
import { RestStopPond } from "GameObjects/RestStopGame/RestStopPond";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { Background } from "GameObjects/UI/Background";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { Text } from "GameObjects/UI/Text";
import RestStopCompletePopup from "Popups/RestStopCompletePopup";
import { PictographIcon } from "GameObjects/RestStopGame/PictographIcon";
import { LayoutNineSliceSprite } from "GameObjects/UI/LayoutNineSliceSprite";
import { RestStopGraphTypeToggleButton } from "GameObjects/RestStopGame/RestStopGraphTypeToggleButton";
import { IPondData } from "../Data/IPondData";
import { ResourceType } from "../Data/ResourceType";
import { StressorType } from "../Data/StressorType";
import { templateResources } from "../Data/templateResources";
import { templateStressors } from "../Data/templateStressors";
import { templatePonds } from "../Data/templatePonds";
import { ITallyData } from "../Data/ITallyData";
import { Application } from "Application";
import { FreePlayMenu } from "States/FreePlayMenu";
import RestStopFreePlayCompletePopup from "Popups/RestStopFreePlayCompletePopup";

const TEXT_BG_WIDTH = 120;

const pondData: IPondData[] = [
    {
        resources: [],
        stressors: [],
        spawnpoints: "",
    },
    {
        resources: [],
        stressors: [],
        spawnpoints: "",
    },
];

declare type GraphType = "bargraph" | "pictograph";
declare type GraphSource = "Food" | "Problems";

export class RestStopGame extends AbstractGameState<typeof RestStopGameLayout> implements ReturnType<typeof RestStopGameLayout> {
    public static readonly NAME = "RestStopGame";

    public static graphType: GraphType = "bargraph";
    public static firstTime: boolean = true;
    protected static graphSource: GraphSource = "Food";
    public readonly layout = RestStopGameLayout;

    public readonly bg: LayoutSprite;
    public readonly barGraphBg: LayoutSprite;
    public readonly pictoGraphBg: LayoutSprite;
    public readonly barGraphButton: RestStopGraphTypeToggleButton;
    public readonly pictoGraphButton: RestStopGraphTypeToggleButton;
    public readonly resourcesBarGraphBar: LayoutNineSliceSprite[];
    public readonly stressorsBarGraphBar: LayoutNineSliceSprite[];
    public readonly pictoGraphColumnOneIconBg: PictographIcon[];
    public readonly pictoGraphColumnTwoIconBg: PictographIcon[];
    public readonly pictoGraphColumnThreeIconBg: PictographIcon[];
    public readonly pictoGraphXAxisIcon: LayoutSprite[];
    public readonly barGraphXAxisIcon: LayoutSprite[];
    public readonly pictoGraphPlusButton: LayoutButton[];
    public readonly barGraphPlusButton: LayoutButton[];
    public readonly barGraphTitleBg: LayoutSprite;
    public readonly pictoGraphTitleBg: LayoutSprite;
    public readonly compareGraphPictographBg: LayoutSprite[];
    public readonly compareGraphBargraphBg: LayoutSprite[];
    public readonly compareGraphStressorsTitleBg: LayoutNineSliceSprite[];
    public readonly sadFace: LayoutSprite[];
    public readonly compareGraphResourcesTitleBg: LayoutNineSliceSprite[];
    public readonly happyFace: LayoutSprite[];
    public readonly comparePondTitleBg: LayoutNineSliceSprite[];
    public readonly compareGraphBarGraphStressorBar: LayoutNineSliceSprite[];
    public readonly compareGraphBarGraphResourceBar: LayoutNineSliceSprite[];
    public readonly compareGraphBarGraphStressorsXAxisIcon: LayoutSprite[];
    public readonly compareGraphBarGraphResourcesXAxisIcon: LayoutSprite[];

    public readonly compareGraphPictoGraphPondOneStressorsBarOneIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondOneStressorsBarTwoIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondOneStressorsBarThreeIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondOneResourcesBarOneIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondOneResourcesBarTwoIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondOneResourcesBarThreeIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoStressorsBarOneIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoStressorsBarTwoIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoStressorsBarThreeIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoResourcesBarOneIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoResourcesBarTwoIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphPondTwoResourcesBarThreeIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphStressorsXAxisIconBg: PictographIcon[];
    public readonly compareGraphPictoGraphResourcesXAxisIconBg: PictographIcon[];
    protected comparePondTitleText1?: Text;
    protected comparePondTitleText2?: Text;

    protected readonly backgroundContainer = this.addChildAt(new Background(), 0);

    protected readonly pondPreview1 = this.addChild(new RestStopPond(() => this.onSelectPondButtonClicked(0), undefined as any as string, SFX.UI_CLICK));
    protected readonly pondPreview2 = this.addChild(new RestStopPond(() => this.onSelectPondButtonClicked(1), undefined as any as string, SFX.UI_CLICK));
    protected pictoGraphContainer = this.addChild(new PIXI.Container());
    protected barGraphContainer = this.addChild(new PIXI.Container());

    protected compareGraphContainer = this.addChild(new PIXI.Container());
    protected compareGraphBarGraphContainer = this.compareGraphContainer.addChild(new PIXI.Container());
    protected compareGraphPictoGraphContainer = this.compareGraphContainer.addChild(new PIXI.Container());

    protected barGraphTitleText: Text;
    protected pictoGraphTitleText: Text;

    protected compareGraphResourcesTitleText: Text[];
    protected compareGraphStressorsTitleText: Text[];
    protected tallyPondTitleBg: LayoutNineSliceSprite;
    protected tallyPondTitleText: Text;

    protected ponds = [
        this.pondPreview1,
        this.pondPreview2,
    ]

    protected focusedPond?: number;

    protected resourceButtons: ResourceButton[] = [];
    protected stressorButtons: StressorButton[] = [];

    protected ducks!: IRuntimeDuck[];

    protected numberOfRestStopsCompleted: number = 0;

    protected bgm = HLF.Application.instance.audio.getAudioTrack("reststoploop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("reststoploop", HLF.AudioCategory.BGM);

    private didClickPictographButton: boolean = false;
    private idleTimeline?: TimelineMax;
    private transitionTimeline?: TimelineLite;

    public constructor() {
        super();
        this.bg = new LayoutSprite();
        this.barGraphBg = new LayoutSprite();
        this.pictoGraphBg = new LayoutSprite();
        this.pictoGraphButton = new RestStopGraphTypeToggleButton(this.onPictoGraphButtonClicked.bind(this), VO.REST_STOP_BUTTON_PICTOGRAPH, SFX.UI_SELECT);
        this.barGraphButton = new RestStopGraphTypeToggleButton(this.onBarGraphButtonClicked.bind(this), VO.REST_STOP_BUTTON_BAR_GRAPH, SFX.UI_SELECT);
        this.resourcesBarGraphBar = [new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12)];
        this.stressorsBarGraphBar = [new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12)];
        this.pictoGraphPlusButton = [
            new LayoutButton(()=>this.onPlusButtonClicked(0), undefined as any as string, SFX.UI_CLICK),
            new LayoutButton(()=>this.onPlusButtonClicked(1), undefined as any as string, SFX.UI_CLICK),
            new LayoutButton(()=>this.onPlusButtonClicked(2), undefined as any as string, SFX.UI_CLICK),
        ];
        this.barGraphPlusButton = [
            new LayoutButton(()=>this.onPlusButtonClicked(0), undefined as any as string, SFX.UI_CLICK),
            new LayoutButton(()=>this.onPlusButtonClicked(1), undefined as any as string, SFX.UI_CLICK),
            new LayoutButton(()=>this.onPlusButtonClicked(2), undefined as any as string, SFX.UI_CLICK),
        ];
        this.pictoGraphColumnOneIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.pictoGraphColumnTwoIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.pictoGraphColumnThreeIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.pictoGraphXAxisIcon = [new LayoutSprite(), new LayoutSprite(), new LayoutSprite()];
        this.barGraphXAxisIcon = [new LayoutSprite(), new LayoutSprite(), new LayoutSprite()];
        this.barGraphTitleBg = new LayoutSprite();
        this.pictoGraphTitleBg = new LayoutSprite();

        this.compareGraphPictographBg = [new LayoutSprite(), new LayoutSprite() ];
        this.compareGraphBargraphBg = [new LayoutSprite(), new LayoutSprite() ];
        this.compareGraphStressorsTitleBg = [
            new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5),
            new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5),
        ];
        this.sadFace = [new LayoutSprite(), new LayoutSprite() ];
        this.compareGraphResourcesTitleBg = [
            new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5),
            new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5),
        ];
        this.happyFace = [new LayoutSprite(), new LayoutSprite() ];
        this.comparePondTitleBg = [new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5), new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5)];
        this.compareGraphBarGraphStressorBar = [
            new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12),
            new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12),
        ];
        this.compareGraphBarGraphResourceBar = [
            new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12),
            new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12), new LayoutNineSliceSprite(4, 4, 4, 12),
        ];
        this.compareGraphBarGraphStressorsXAxisIcon = [
            new LayoutSprite(), new LayoutSprite(), new LayoutSprite(),
            new LayoutSprite(), new LayoutSprite(), new LayoutSprite(),
        ];
        this.compareGraphBarGraphResourcesXAxisIcon = [
            new LayoutSprite(), new LayoutSprite(), new LayoutSprite(),
            new LayoutSprite(), new LayoutSprite(), new LayoutSprite(),
        ];
        this.compareGraphPictoGraphPondOneStressorsBarOneIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondOneStressorsBarTwoIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondOneStressorsBarThreeIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondOneResourcesBarOneIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondOneResourcesBarTwoIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondOneResourcesBarThreeIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoStressorsBarOneIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoStressorsBarTwoIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoStressorsBarThreeIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoResourcesBarOneIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoResourcesBarTwoIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphPondTwoResourcesBarThreeIconBg = [new PictographIcon(), new PictographIcon(), new PictographIcon(), new PictographIcon()];
        this.compareGraphPictoGraphStressorsXAxisIconBg = [
            new PictographIcon(), new PictographIcon(), new PictographIcon(),
            new PictographIcon(), new PictographIcon(), new PictographIcon(),
        ];
        this.compareGraphPictoGraphResourcesXAxisIconBg = [
            new PictographIcon(), new PictographIcon(), new PictographIcon(),
            new PictographIcon(), new PictographIcon(), new PictographIcon(),
        ];

        this.barGraphTitleText = new Text("Food", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: false});
        this.pictoGraphTitleText = new Text("Food", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: false});

        this.compareGraphResourcesTitleText = [
            new Text("Food", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0, 0.5)),
            new Text("Food", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0, 0.5)),
        ];

        this.compareGraphStressorsTitleText = [
            new Text("Problems", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0, 0.5)),
            new Text("Problems", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0, 0.5)),
        ];

        [...this.compareGraphResourcesTitleText, ...this.compareGraphStressorsTitleText].forEach(t=>{
            t.align = "left";
        });

        // didn't feel like updating the .fla so here it is
        this.tallyPondTitleText = new Text("pond name fpo", Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0.5, 0.5));
        this.tallyPondTitleBg = new LayoutNineSliceSprite(22.5, 22.5, 22.5, 22.5);
        this.tallyPondTitleBg.setLayoutTexture("cyberchase-reststop-comp-pondtitlebg", 0.5, 0.5);
    }

    public static GenerateRandomPond(pSquares: string): IPondData {
        const minStressorsPerType = 1;
        const maxStressorsPerType = 4;
        const minResourcesPerType = 1;
        const maxResourcesPerType = 4;

        const maxTotalResources = (pSquares.match(/F/g) || []).length;
        const maxTotalStressors = (pSquares.match(/P/g) || []).length;

        let maxTotalResourcesPlusStressors: number;
        switch (Settings.difficulty) {
            default:
            case 0:
            case 1/3:
                maxTotalResourcesPlusStressors = 6;
                break;
            case 2/3:
                maxTotalResourcesPlusStressors = 9;
                break;
            case 3/3:
                maxTotalResourcesPlusStressors = 12;
                break;
        }

        const ret: IPondData = {
            resources: [],
            stressors: [],
            spawnpoints: pSquares,
        };

        const stressorTypes: StressorType[] = ["dog", "dozer", "plastic", "oildrum", "truck"];
        const resourceTypes: ResourceType[] = ["aquaticplant", "berries", "corn", "flower", "lilypad"];

        HLF.ArrayUtils.shuffle(stressorTypes);
        stressorTypes.splice(3); // remove all but 3

        HLF.ArrayUtils.shuffle(resourceTypes);
        resourceTypes.splice(3); // remove all but 3

        stressorTypes.forEach(stressorType => {
            for (let i = 0; i < HLF.Random.intBetween(minStressorsPerType, maxStressorsPerType + 1); i++) {
                if (ret.stressors.length < maxTotalStressors) {
                    ret.stressors.push({
                        type: stressorType,
                    });
                }
            }
        })

        resourceTypes.forEach(resourceType => {
            for (let i = 0; i < HLF.Random.intBetween(minResourcesPerType, maxResourcesPerType + 1); i++) {
                if (ret.resources.length < maxTotalResources) {
                    ret.resources.push({
                        type: resourceType,
                    });
                }
            }
        });

        HLF.ArrayUtils.shuffle(ret.resources);
        HLF.ArrayUtils.shuffle(ret.stressors);

        while ((ret.resources.length + ret.stressors.length) > maxTotalResourcesPlusStressors) {
            if (HLF.Random.bool() === true && ret.resources.length > 2) {
                ret.resources.pop();
            }
            else if (ret.stressors.length > 2) {
                ret.stressors.pop();
            }
            else {
                maxTotalResourcesPlusStressors++; // ignore max total so we can have minimum 2 of each
            }
        }

        return ret;
    }

    public init(pSize: PIXI.Point, pData: { name: string, ducks: IRuntimeDuck[], restStopsCompleted?: number }) {
        super.init(pSize, pData);
        this.backgroundContainer.addChild(this.bg);
        this.addChild(this.pictoGraphButton);
        this.addChild(this.barGraphButton);

        this.barGraphContainer.addChild(this.barGraphBg);
        this.pictoGraphContainer.addChild(this.pictoGraphBg);
        this.barGraphContainer.addChild(...this.resourcesBarGraphBar);
        this.barGraphContainer.addChild(...this.stressorsBarGraphBar);
        this.pictoGraphContainer.addChild(...this.pictoGraphPlusButton);
        this.barGraphContainer.addChild(...this.barGraphPlusButton);
        this.pictoGraphContainer.addChild(...this.pictoGraphColumnOneIconBg);
        this.pictoGraphContainer.addChild(...this.pictoGraphColumnTwoIconBg);
        this.pictoGraphContainer.addChild(...this.pictoGraphColumnThreeIconBg);
        this.pictoGraphContainer.addChild(...this.pictoGraphXAxisIcon);
        this.barGraphContainer.addChild(...this.barGraphXAxisIcon);
        this.barGraphContainer.addChild(this.barGraphTitleBg);
        this.pictoGraphContainer.addChild(this.pictoGraphTitleBg);
        this.barGraphTitleBg.addChild(this.barGraphTitleText);
        this.pictoGraphTitleBg.addChild(this.pictoGraphTitleText);

        this.compareGraphPictographBg.forEach(bg=>this.compareGraphPictoGraphContainer.addChildAt(bg, 0));
        this.compareGraphBargraphBg.forEach(bg=>this.compareGraphBarGraphContainer.addChildAt(bg, 0));
        this.compareGraphContainer.addChild(...this.compareGraphStressorsTitleBg);
        this.compareGraphContainer.addChild(...this.compareGraphResourcesTitleBg);
        this.compareGraphContainer.addChild(...this.comparePondTitleBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneStressorsBarOneIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneStressorsBarTwoIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneStressorsBarThreeIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneResourcesBarOneIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneResourcesBarTwoIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondOneResourcesBarThreeIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoStressorsBarOneIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoStressorsBarTwoIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoStressorsBarThreeIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoResourcesBarOneIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoResourcesBarTwoIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphPondTwoResourcesBarThreeIconBg);
        this.compareGraphResourcesTitleBg.forEach((bg, i)=>bg.addChild(this.compareGraphResourcesTitleText[i]));
        this.compareGraphResourcesTitleBg.forEach((bg, i)=>this.compareGraphResourcesTitleBg[i].addChild(this.happyFace[i]));
        this.compareGraphStressorsTitleBg.forEach((bg, i)=>bg.addChild(this.compareGraphStressorsTitleText[i]));
        this.compareGraphStressorsTitleBg.forEach((bg, i)=>this.compareGraphStressorsTitleBg[i].addChild(this.sadFace[i]));
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphStressorsXAxisIconBg);
        this.compareGraphPictoGraphContainer.addChild(...this.compareGraphPictoGraphResourcesXAxisIconBg);
        this.compareGraphBarGraphContainer.addChild(...this.compareGraphBarGraphStressorBar);
        this.compareGraphBarGraphContainer.addChild(...this.compareGraphBarGraphResourceBar);
        this.compareGraphBarGraphContainer.addChild(...this.compareGraphBarGraphStressorsXAxisIcon);
        this.compareGraphBarGraphContainer.addChild(...this.compareGraphBarGraphResourcesXAxisIcon);
        this.addChild(this.tallyPondTitleBg);
        this.tallyPondTitleBg.addChild(this.tallyPondTitleText);
        this.tallyPondTitleBg.position.set(330, 100);
        this.tallyPondTitleBg.visible = false;

        if (pData.ducks === undefined) {
            console.log("initializing ducks with new data");
            let duckCount = 5;
            if (Settings.difficulty === (2/3)) {
                duckCount = 7;
            }
            else if (Settings.difficulty === (3/3)) {
                duckCount = 9;
            }
            pData.ducks = GameState.GetDucks(duckCount);
            HLF.ArrayUtils.shuffle(pData.ducks);
        }
        else {
            console.log("got ducks data from previous scene");
        }

        this.ducks = pData.ducks;

        this.numberOfRestStopsCompleted = pData.restStopsCompleted ?? 0;

        this.initPonds();

        this.tallyPondTitleBg.setNineSliceScale(this.tallyPondTitleText.textWidth / TEXT_BG_WIDTH, 1);

        this.compareGraphResourcesTitleBg.forEach((bg, i)=>{
            bg.setNineSliceScale(Math.max((this.compareGraphResourcesTitleText[i].textWidth + this.happyFace[i].width) / TEXT_BG_WIDTH, 1), 1);
            this.compareGraphResourcesTitleText[i].position.x = (-this.compareGraphResourcesTitleText[i].textWidth / 2) - (this.happyFace[i].width / 2);
            this.happyFace[i].x = this.compareGraphResourcesTitleText[i].position.x + this.compareGraphResourcesTitleText[i].textWidth + this.happyFace[i].width / 2;
            this.happyFace[i].y = 0;
        });
        this.compareGraphStressorsTitleBg.forEach((bg, i)=>{
            bg.setNineSliceScale((this.compareGraphStressorsTitleText[i].textWidth + this.sadFace[i].width) / TEXT_BG_WIDTH, 1);
            this.compareGraphStressorsTitleText[i].position.x = (-this.compareGraphStressorsTitleText[i].textWidth / 2) - (this.sadFace[i].width / 2);
            this.sadFace[i].x = this.compareGraphStressorsTitleText[i].position.x + this.compareGraphStressorsTitleText[i].textWidth + this.sadFace[i].width / 2;
            this.sadFace[i].y = 0;
        });

        this.pictoGraphContainer.visible = false;
        this.barGraphContainer.visible = false;
        this.compareGraphContainer.visible = false;

        if (RestStopGame.graphType === "pictograph") {
            this.barGraphButton.setInteractive(true);
            this.pictoGraphButton.setInteractive(false);
        }
        else {
            this.pictoGraphButton.setInteractive(true);
            this.barGraphButton.setInteractive(false);
        }

        this.pictoGraphButton.visible = false;
        this.barGraphButton.visible = false;
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);

        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
            ...this.resourceButtons,
            ...this.stressorButtons,
            ...this.barGraphPlusButton,
            ...this.pictoGraphPlusButton,
            this.pictoGraphButton,
            this.barGraphButton,
        ]);


        if (RestStopGame.firstTime) {
            console.log("first time at rest stop -- onboarding starts now");
            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_ONBOARDING_1, {priority: 1});
            Application.instance.analytics.event("tutorial_begin", RestStopGame.NAME);
        }

        TweenLite.delayedCall(1.5, ()=>{
            this.startTallyForPond(0);
        });
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    public gotoNextState(name: string, data?: object) {
        super.gotoNextState(name, { ducks: this.ducks, restStopsCompleted: this.numberOfRestStopsCompleted, ...data });
    }

    public destroy(pOptions: Parameters<typeof HLF.State.prototype.destroy>[0]) {
        this.idleTimeline?.kill();
        this.transitionTimeline?.kill();
        super.destroy(pOptions);
    }
    protected playHelpVO() {
        if (this.focusedPond === undefined) {
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_HELP_COMPARE]);
        }
        else {
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_HELP_TALLY]);
        }
    }

    protected hidePond(pPond: number) {
        return new Promise<void>((resolve, reject) => {
            PubSub.publishSync(HLF.UNREGISTER_FOCUSABLES,
                [
                    ...this.resourceButtons,
                    ...this.stressorButtons,
                    ...this.barGraphPlusButton,
                    ...this.pictoGraphPlusButton,
                ]);
            const selectedPond = pPond === 0 ? this.pondPreview1 : this.pondPreview2;
            const notSelectedPond = pPond !== 0 ? this.pondPreview1 : this.pondPreview2;

            this.transitionTimeline?.kill();
            this.transitionTimeline = new TimelineLite({ delay: 1 });
            this.transitionTimeline
                .staggerTo([...this.resourceButtons, ...this.stressorButtons], 0.5, {
                    pixi: {
                        scale: 0,
                        alpha: 0,
                    },
                }, 0.05)
                .to(selectedPond, 0.5, {
                    pixi: {
                        scale: 0,
                        alpha: 0,
                    },
                })
                .to(this.tallyPondTitleBg, 0.5, {pixi: {y: -this.tallyPondTitleBg.height / 2}})
                .to([this.barGraphContainer, this.pictoGraphContainer], 0.5, {
                    pixi: {
                        y: 1024,
                    }
                })
                .call(() => {
                    this.tallyPondTitleBg.visible = false;
                    this.barGraphContainer.visible = false;
                    this.pictoGraphContainer.visible = false;
                    this.barGraphContainer.y = 0;
                    this.pictoGraphContainer.y = 0;

                    selectedPond.visible = false;
                    selectedPond.scale.set(1);
                    selectedPond.alpha = 1;
                    this.stressorButtons.forEach(s => {
                        s.scale.set(1);
                        s.alpha = 1;
                        s.outline.enabled = false;
                        s.checkMark.visible = false;
                        selectedPond.addChild(s);
                    });
                    this.resourceButtons.forEach(r => {
                        r.scale.set(1);
                        r.alpha = 1;
                        r.outline.enabled = false;
                        r.checkMark.visible = false;
                        selectedPond.addChild(r);
                    });

                    this.stressorButtons = [];
                    this.resourceButtons = [];

                    resolve();
                });

        });
    }

    protected onPlusButtonClicked(pIndex: number): void {
        if (RestStopGame.graphSource === "Food") {
            const resourceType = Object.keys(this.ponds[this.focusedPond!].tally.resources)[pIndex] as ResourceType;
            const nextButton: ResourceButton | undefined = this.resourceButtons.find(b=>b.resourceType === resourceType && b.tallied === false);
            if (nextButton) {
                nextButton.onFocusActivated();
            }
            else {
                HLF.Application.instance.audio.play(SFX.DEAD_CLICK, 1, false, HLF.AudioCategory.SFX);
                HLF.Application.instance.voiceover.playVO(VO.REST_STOP_NOMORE);
                this.checkIfAllItemsTallied();
            }
        }
        else {
            const stressorType = Object.keys(this.ponds[this.focusedPond!].tally.stressors)[pIndex] as StressorType;
            const nextButton: StressorButton | undefined = this.stressorButtons.find(b=>b.stressorType === stressorType && b.tallied === false);
            if (nextButton) {
                nextButton.onFocusActivated();
            }
            else {
                HLF.Application.instance.audio.play(SFX.DEAD_CLICK, 1, false, HLF.AudioCategory.SFX);
                HLF.Application.instance.voiceover.playVO(VO.REST_STOP_NOMORE);
                this.checkIfAllItemsTallied();
            }
        }
    }

    protected startTallyForPond(pPond: number) {
        this.focusedPond = pPond;
        RestStopGame.graphSource = "Food";

        PubSub.publishSync(HLF.CLEAR_FOCUS);

        const selectedPond = pPond === 0 ? this.pondPreview1 : this.pondPreview2;
        const notSelectedPond = pPond !== 0 ? this.pondPreview1 : this.pondPreview2;

        this.tallyPondTitleText.setText(selectedPond.name);
        this.tallyPondTitleBg.setNineSliceScale(this.tallyPondTitleText.textWidth / TEXT_BG_WIDTH, 1);

        HLF.ArrayUtils.shuffle(pondData[pPond].resources);
        HLF.ArrayUtils.shuffle(pondData[pPond].stressors);

        // draw resources
        for (let i = 0; i < pondData[pPond].resources.length; i++) {
            const resourceButton: ResourceButton = new ResourceButton (
                () => this.onResourceButtonClicked(resourceButton, selectedPond),
                templateResources[pondData[pPond].resources[i].type].sprite,
                templateResources[pondData[pPond].resources[i].type].sprite,
                templateResources[pondData[pPond].resources[i].type].sprite,
                templateResources[pondData[pPond].resources[i].type].hoverVO,
                SFX.TALLY,
            );
            resourceButton.alpha = 0;
            resourceButton.resourceType = pondData[pPond].resources[i].type;
            this.resourceButtons.push(this.addChild(resourceButton));
            if (selectedPond.tally.resources[resourceButton.resourceType] === undefined) {
                selectedPond.tally.resources[resourceButton.resourceType] = 0;
            }
            selectedPond.addChild(resourceButton);
        }

        // draw stressors
        for (let i = 0; i < pondData[pPond].stressors.length; i++) {
            const stressorButton: StressorButton = new StressorButton(
                () => this.onStressorButtonClicked(stressorButton, selectedPond),
                templateStressors[pondData[pPond].stressors[i].type].sprite,
                templateStressors[pondData[pPond].stressors[i].type].sprite,
                templateStressors[pondData[pPond].stressors[i].type].sprite,
                templateStressors[pondData[pPond].stressors[i].type].hoverVO,
                SFX.TALLY,
            );
            stressorButton.alpha = 0;
            stressorButton.stressorType = pondData[pPond].stressors[i].type;
            this.stressorButtons.push(this.addChild(stressorButton));
            if (selectedPond.tally.stressors[stressorButton.stressorType] === undefined) {
                selectedPond.tally.stressors[stressorButton.stressorType] = 0;
            }
            stressorButton.setInteractive(false);
            stressorButton.outline.enabled = false;
            selectedPond.addChild(stressorButton);
        }

        let x = 0;
        let y = 0;
        let f = 0; // F for Food aka Resources
        let p = 0; // P for Problems aka Stressors
        let sb = 0; // stressorbutton
        let rb = 0; // resourcebutton

        const totalResourceSpawnPoints = (pondData[pPond].spawnpoints.match(/F/g) || []).length;
        const totalStressorSpawnPoints = (pondData[pPond].spawnpoints.match(/P/g) || []).length;
        const totalResources = this.resourceButtons.length;
        const totalStressors = this.stressorButtons.length;

        for (let i = 0; i < pondData[pPond].spawnpoints.length; i++) {
            const char = pondData[pPond].spawnpoints.charAt(i);
            if (char === "P") {
                const stressorButtonsRemaining = (totalStressors - sb);
                const stressorSpawnPointsRemaining = (totalStressorSpawnPoints - p);
                const canSkip = stressorSpawnPointsRemaining > stressorButtonsRemaining;
                const doSkip = canSkip && HLF.Random.bool();

                if (!doSkip && this.stressorButtons[sb]) {
                    this.stressorButtons[sb].x = x * (selectedPond.image!.width / 6) - selectedPond.image!.width / 2 + (selectedPond.image!.width / 6 / 2);
                    this.stressorButtons[sb].y = y * (selectedPond.image!.height / 6) - selectedPond.image!.height / 2 + (selectedPond.image!.height / 6 / 2);
                    sb++;
                }
                p++;
            }
            else if (char === "F") {
                const resourceButtonsRemaining = (totalResources - rb);
                const resourceSpawnPointsRemaining = (totalResourceSpawnPoints - f);
                const canSkip = resourceSpawnPointsRemaining > resourceButtonsRemaining;
                const doSkip = canSkip && HLF.Random.bool();

                if (!doSkip && this.resourceButtons[rb]) {
                    this.resourceButtons[rb].x = x * (selectedPond.image!.width / 6) - selectedPond.image!.width / 2 + (selectedPond.image!.width / 6 / 2);
                    this.resourceButtons[rb].y = y * (selectedPond.image!.height / 6) - selectedPond.image!.height / 2 + (selectedPond.image!.height / 6 / 2);
                    rb++;
                }
                f++;
            }
            x++;
            if (x >= 6 ) {
                x = 0;
                y++;
            }
        }

        this.drawFullscreenGraph(selectedPond);

        selectedPond.visible = true;

        this.transitionTimeline?.kill();
        this.transitionTimeline = new TimelineLite();

        this.transitionTimeline?.kill();
        this.transitionTimeline = new TimelineLite();
        this.transitionTimeline.to(selectedPond, 0.5, {
            pixi: {
                x: 340,
                Y: 384,
                scale: 1,
                alpha: 1,
            },
        }).to(this.resourceButtons, 0.5, {
            pixi: {
                alpha: 1,
            },
        }).to(this.stressorButtons, 0.5, {
            pixi: {
                alpha: 0.5,
            },
        })
        .call(()=>{
            if (RestStopGame.graphType === "bargraph") {
                this.barGraphContainer.visible = true;
            }
            else {
                this.pictoGraphContainer.visible = true;
            }
            this.barGraphButton.visible = true;
            this.pictoGraphButton.visible = true;
        })
        .from([this.barGraphContainer, this.pictoGraphContainer], 0.5, {
            pixi: {
                x: 1024,
            },
        })
        .from([this.barGraphButton, this.pictoGraphButton], 0.5, {
            pixi: {
                y: 1024,
            },
        })
        .call(()=>{
            TweenLite.fromTo(this.tallyPondTitleBg, 0.5, {pixi: {y: -this.tallyPondTitleBg.height / 2}}, {pixi: {y: 100}});
            this.tallyPondTitleBg.visible = true;
            this.onPondFocused(pPond);

            this.checkIfAllItemsTallied();
            const focusables = [
                ...this.resourceButtons,
                ...this.stressorButtons,
                ...this.barGraphPlusButton,
                ...this.pictoGraphPlusButton,
                this.pictoGraphButton,
                this.barGraphButton,
            ];
            // hack: disable all buttons before registering focusables, so keyboard focus does not immediately jump to first item
            focusables.forEach(button=>button.interactive = false);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
                ...this.resourceButtons,
                ...this.stressorButtons,
                ...this.barGraphPlusButton,
                ...this.pictoGraphPlusButton,
                this.pictoGraphButton,
                this.barGraphButton,
            ]);
            // reenable everything _except_ stressor buttons
            [
                ...this.resourceButtons,
                ...this.barGraphPlusButton,
                ...this.pictoGraphPlusButton,
                this.pictoGraphButton,
                this.barGraphButton,
            ].forEach(button=>button.interactive = true);
        }).to(notSelectedPond, 0.5, {
            pixi: {
                scale: 0,
                alpha: 0,
            },
        }, 0).call(()=>{
            notSelectedPond.visible = false;
            notSelectedPond.scale.set(1);
        }, undefined, undefined, 0.5);


        if (Settings.audioDescriptionEnabled) {
            const voToPlay = [
                VO.REST_STOP_AUDIO_DESCRIPTION_2A,
                this.ponds[pPond].nameVO,
                VO.REST_STOP_AUDIO_DESCRIPTION_2B,
            ];

            let graphTypeVO: string;

            if (RestStopGame.graphType === "bargraph") {
                graphTypeVO = VO.REST_STOP_BUTTON_BAR_GRAPH;
            }
            else {
                graphTypeVO = VO.REST_STOP_BUTTON_PICTOGRAPH;
            }
            voToPlay.push(graphTypeVO);
            voToPlay.push(VO.REST_STOP_AUDIO_DESCRIPTION_2C);

            HLF.Application.instance.voiceover.playVO(voToPlay, {mode: HLF.PlayMode.Append, priority: 4, caption: {
                id: VO.REST_STOP_AUDIO_DESCRIPTION_2A,
                args: {
                    "POND": this.ponds[pPond].nameVO,
                    "GRAPHTYPE": graphTypeVO,
                    "_": VO.REST_STOP_AUDIO_DESCRIPTION_2B, // hack to extend duration of caption
                    "__": VO.REST_STOP_AUDIO_DESCRIPTION_2C, // hack to extend duration of caption
                }
            }});

            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_AUDIO_DESCRIPTION_3, {mode: HLF.PlayMode.Append, priority: 4});

            HLF.Application.instance.voiceover.playVO([
                VO.REST_STOP_AUDIO_DESCRIPTION_4A,
                this.ponds[pPond].nameVO,
                VO.REST_STOP_AUDIO_DESCRIPTION_4B,
                this.ponds[pPond].nameVO,
                VO.REST_STOP_AUDIO_DESCRIPTION_4C,
                this.ponds[pPond].nameVO,
            ], {mode: HLF.PlayMode.Append, priority: 4, caption: {
                id: VO.REST_STOP_AUDIO_DESCRIPTION_4A,
                args: {
                    "POND": this.ponds[pPond].nameVO,
                    "_": VO.REST_STOP_AUDIO_DESCRIPTION_4B, // hack to extend the duration of the caption
                    "__": VO.REST_STOP_AUDIO_DESCRIPTION_4C,
                    "___": this.ponds[pPond].nameVO,
                    "____": this.ponds[pPond].nameVO,
                },
            }});
        }

    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        super.onSettingsChanged(pTopic, pSettings);
        this.compareGraphBargraphBg.forEach(s=>s.interactive = Settings.audioDescriptionEnabled);
        this.compareGraphPictographBg.forEach(s=>s.interactive = Settings.audioDescriptionEnabled);
        setTimeout(()=>{ // one frame later, to make sure text updates first
            this.tallyPondTitleBg.setNineSliceScale(this.tallyPondTitleText.textWidth / TEXT_BG_WIDTH, 1);
            if (this.comparePondTitleText1) {
                this.comparePondTitleBg[0].setNineSliceScale(this.comparePondTitleText1.textWidth / TEXT_BG_WIDTH, 0.6);
                this.comparePondTitleBg[0].scale.set(pSettings.textSizeMultiplier);
            }
            if (this.comparePondTitleText2) {
                this.comparePondTitleBg[1].setNineSliceScale(this.comparePondTitleText2.textWidth / TEXT_BG_WIDTH, 0.6);
                this.comparePondTitleBg[1].scale.set(pSettings.textSizeMultiplier);
            }
            this.compareGraphResourcesTitleBg.forEach((bg, i)=>{
                this.compareGraphResourcesTitleText[i].position.x = (-this.compareGraphResourcesTitleText[i].textWidth / 2) - (this.happyFace[i].width / 2);
                bg.setNineSliceScale(Math.max((this.compareGraphResourcesTitleText[i].textWidth + this.happyFace[i].width) / TEXT_BG_WIDTH, 1), 1);
                this.happyFace[i].x = this.compareGraphResourcesTitleText[i].position.x + this.compareGraphResourcesTitleText[i].textWidth + this.happyFace[i].width;
            });
            this.compareGraphStressorsTitleBg.forEach((bg, i)=>{
                this.compareGraphStressorsTitleText[i].position.x = (-this.compareGraphStressorsTitleText[i].textWidth / 2) - (this.sadFace[i].width / 2);
                bg.setNineSliceScale((this.compareGraphStressorsTitleText[i].textWidth + this.sadFace[i].width) / TEXT_BG_WIDTH, 1);
                this.sadFace[i].x = this.compareGraphStressorsTitleText[i].position.x + this.compareGraphStressorsTitleText[i].textWidth + this.sadFace[i].width;
            });
        }, 0);
    }

    protected onSelectPondButtonClicked(pPond: number) {
        console.log("clicked pond " + pPond);
        this.pondPreview1.interactive = false;
        this.pondPreview2.interactive = false;

        this.pondPreview1.stopPulse();
        this.pondPreview2.stopPulse();

        let r = pondData[pPond].resources.length; // total number of resources

        const oldDucks = this.ducks.map(d => {
            return {energy: d.energy};
        });

        // feed ducks with energy at zero first
        for (let i = 0; i < this.ducks.length; i++) {
            if (this.ducks[i].energy === 0) {
                if (r >= 1) {
                    r--;
                    this.ducks[i].energy = 100;
                    this.ducks[i].efficiency = 100;
                }
            }
        }

        // feed ducks with 80% or less energy next
        for (let i = 0; i < this.ducks.length; i++) {
            if (this.ducks[i].energy <= 80) {
                if (r >= 1) {
                    r--;
                    this.ducks[i].energy = 100;
                    this.ducks[i].efficiency = 100;
                }
            }
        }

        // feed rest of ducks
        for (let i = 0; i < this.ducks.length; i++) {
            if (this.ducks[i].energy < 100) {
                if (r >= 1) {
                    r--;
                    this.ducks[i].energy = 100;
                    this.ducks[i].efficiency = 100;
                }
            }
        }

        this.numberOfRestStopsCompleted++;

        this.interactiveChildren = false;

        console.log(pondData[pPond].resources.length, pondData[pPond].resources.map(res=>res.type.toString()));
        const correct = pondData[pPond].resources.length > pondData[1-pPond].resources.length;
        if (correct !== true) {
            GameState.perfectPonds = false;
        }
        this.stopIdleTimer();
        PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken (
            RestStopCompletePopup.NAME,
            this.onRestStopCompletePopupClosed.bind(this),
            "static", false,
            {
                pondName: this.ponds[pPond].name,
                ducks: this.ducks.map((d, i) => Object.assign(d, {previousEnergy: oldDucks[i].energy})),
                firstTime: RestStopGame.firstTime,
                resources: pondData[pPond].resources,
                resourceCount: (pondData[pPond].resources.length),
                correct,
            },
        ));

        HLF.Application.instance.voiceover.stopVO();

        if (correct) {
            HLF.Application.instance.audio.play(SFX.GOOD_PICK, 1, false, HLF.AudioCategory.SFX);
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_COMPLETE_CORRECT_ANSWER]);
        }
        else {
            HLF.Application.instance.audio.play(SFX.BAD_PICK, 1, false, HLF.AudioCategory.SFX);
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_COMPLETE_WRONG_ANSWER]);
        }

        if (Settings.audioDescriptionEnabled) {
            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_AUDIO_DESCRIPTION_CONTINUE, HLF.PlayMode.Append);
        }

        Application.instance.analytics.event("select_content", this.ponds[pPond].name);
    }

    protected onRestStopCompletePopupClosed() {
        if (RestStopGame.firstTime) {
            RestStopGame.firstTime = false;
            console.log("rest stop onboarding complete");
            Application.instance.analytics.event("tutorial_complete", RestStopGame.NAME);
        }
        this.interactiveChildren = false;
        if (GameState.freePlay) {
            PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(RestStopFreePlayCompletePopup.NAME, ()=>{
                /* noop */
            }, "static", false));
        }
        else {
            PubSub.publishSync(HLF.UNREGISTER_ALL_FOCUSABLES);
            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_COMPLETE, ()=>{
                this.gotoNextState(FlyingGame.NAME);
            });
        }
    }

    protected onResourceButtonClicked(pButton: ResourceButton, pPond: RestStopPond) {
        this.restartIdleTimer();
        this.resourceButtons.forEach(b=>b.stopPulse());
        if (pButton.tallied) {
            return;
        }
        pButton.tallied = true;
        pButton.setInteractive(false);
        pButton.checkMark.visible = true;
        pButton.outline.enabled = false;

        if (pPond.tally.resources[pButton.resourceType!] !== undefined) {
            pPond.tally.resources[pButton.resourceType!]!++;
        }
        else {
            pPond.tally.resources[pButton.resourceType!] = 1;
        }

        this.drawFullscreenGraph(pPond);
        const focusNextResource = ()=>{
            const nextButton: ResourceButton | StressorButton | undefined = this.resourceButtons.find(b => b.tallied === false);
            if (nextButton) {
                if (Settings.audioDescriptionEnabled) {
                    HLF.Application.instance.voiceover.playVO(nextButton.hoverVO, HLF.PlayMode.Append);
                }
                PubSub.publishSync(HLF.FORCE_FOCUS, nextButton);
            }
        }
        if (Settings.audioDescriptionEnabled) {
            if (RestStopGame.graphType === "pictograph") {
                HLF.Application.instance.voiceover.playVO([VO.REST_STOP_AUDIO_DESCRIPTION_5A, pButton.hoverVO, VO.REST_STOP_AUDIO_DESCRIPTION_5B], {priority: 5, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_5A,
                    args: {
                        "ICON": pButton.hoverVO,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_5B, // hack to extend the duration of the caption
                    },
                }, callback: () => {
                    focusNextResource();
                    this.checkIfAllItemsTallied();
                }});
            }
            else {
                const vosToPlay: (string|number)[] = [VO.REST_STOP_AUDIO_DESCRIPTION_6A, pButton.hoverVO, VO.REST_STOP_AUDIO_DESCRIPTION_6B];
                const height = pPond.tally.resources[pButton.resourceType!];
                let numberVO: string;
                let unitsVO: string;
                switch (height) {
                    case 1:
                        numberVO = VO.REST_STOP_NUMBER_ONE;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_BARGRAPH_FOOD_TALLY_UPDATED_D_ALT;
                        break;
                    case 2:
                        numberVO = VO.REST_STOP_NUMBER_TWO;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                    case 3:
                        numberVO = VO.REST_STOP_NUMBER_THREE;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                    case 4:
                        numberVO = VO.REST_STOP_NUMBER_FOUR;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                }

                vosToPlay.push(numberVO!);
                vosToPlay.push(unitsVO!);

                HLF.Application.instance.voiceover.playVO(vosToPlay, {priority: 5, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_6A,
                    args: {
                        "ICON": pButton.hoverVO,
                        "NUMBER": numberVO!,
                        "UNIT": unitsVO!,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_6B, // hack to extend the duration of the caption
                    },
                }, callback: ()=>{
                    focusNextResource();
                    this.checkIfAllItemsTallied();
                }});
            }
        }
        else {
            focusNextResource();
            this.checkIfAllItemsTallied();
        }

    }

    protected onStressorButtonClicked(pButton: StressorButton, pPond: RestStopPond) {
        this.restartIdleTimer();
        this.stressorButtons.forEach(b=>b.stopPulse());
        if (pButton.tallied) {
            return;
        }
        pButton.tallied = true;
        pButton.setInteractive(false);
        pButton.checkMark.visible = true;
        pButton.outline.enabled = false;
        if (pPond.tally.stressors[pButton.stressorType!] !== undefined) {
            pPond.tally.stressors[pButton.stressorType!]!++;
        }
        else {
            pPond.tally.stressors[pButton.stressorType!] = 1;
        }

        this.drawFullscreenGraph(pPond);

        const focusNextStressor = () => {
            const nextButton: StressorButton | ResourceButton | undefined = this.stressorButtons.find(b => b.tallied === false);
            if (nextButton) {
                if (Settings.audioDescriptionEnabled) {
                    HLF.Application.instance.voiceover.playVO(nextButton.hoverVO, HLF.PlayMode.Append);
                }
                PubSub.publishSync(HLF.FORCE_FOCUS, nextButton);
            }
        }

        if (Settings.audioDescriptionEnabled) {
            if (RestStopGame.graphType === "pictograph") {
                HLF.Application.instance.voiceover.playVO([VO.REST_STOP_AUDIO_DESCRIPTION_5A, pButton.hoverVO, VO.REST_STOP_AUDIO_DESCRIPTION_5B], {priority: 5, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_5A,
                    args: {
                        "ICON": pButton.hoverVO,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_5B, // hack to extend the duration of the caption
                    }
                }, callback: ()=>{
                    focusNextStressor();
                    this.checkIfAllItemsTallied();
                }});
            }
            else {
                const vosToPlay: (string|number)[] = [VO.REST_STOP_AUDIO_DESCRIPTION_6A, pButton.hoverVO, VO.REST_STOP_AUDIO_DESCRIPTION_6B];
                const height = pPond.tally.stressors[pButton.stressorType!];
                let numberVO: string;
                let unitsVO: string;
                switch (height) {
                    case 1:
                        numberVO = VO.REST_STOP_NUMBER_ONE;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_BARGRAPH_FOOD_TALLY_UPDATED_D_ALT;
                        break;
                    case 2:
                        numberVO = VO.REST_STOP_NUMBER_TWO;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                    case 3:
                        numberVO = VO.REST_STOP_NUMBER_THREE;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                    case 4:
                        numberVO = VO.REST_STOP_NUMBER_FOUR;
                        unitsVO = VO.REST_STOP_AUDIO_DESCRIPTION_6C_ALT;
                        break;
                }

                vosToPlay.push(numberVO!);
                vosToPlay.push(unitsVO!);

                HLF.Application.instance.voiceover.playVO(vosToPlay, {priority: 5, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_6A,
                    args: {
                        "ICON": pButton.hoverVO,
                        "NUMBER": numberVO!,
                        "UNIT": unitsVO!,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_6B, // hack to extend the duration of the caption
                    },
                }, callback: ()=>{
                    focusNextStressor();
                    this.checkIfAllItemsTallied();
                }});
            }
        }
        else {
            focusNextStressor();
            this.checkIfAllItemsTallied();
        }
    }

    protected checkIfAllItemsTallied() {
        const allResourcesTallied = !this.resourceButtons.some(r => r.tallied === false);
        const allStressorsTallied = !this.stressorButtons.some(s => s.tallied === false);
        const anyStressorsTallied = this.stressorButtons.some(s => s.tallied === true);

        if (allResourcesTallied && allStressorsTallied) {
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_PROBLEMS_TALLY_COMPLETE], HLF.PlayMode.Append);
            this.ponds[this.focusedPond!].tallied = true;
            PubSub.publishSync(HLF.CLEAR_FOCUS);

            if (RestStopGame.firstTime && this.focusedPond === 0) {
                HLF.Application.instance.voiceover.playVO([VO.REST_STOP_POND_1_COMPLETE], HLF.PlayMode.Append);
            }

            this.hidePond(this.focusedPond!)
                .then(() => {
                    const nextPond = 1 - this.focusedPond!; // assumes two ponds
                    if (this.ponds[nextPond].tallied === false) {
                        this.startTallyForPond(nextPond);
                    }
                    else {
                        this.focusedPond = undefined;
                        this.onAllPondsTallied();
                    }
                });
        }
        else if (allResourcesTallied && !anyStressorsTallied) {
            this.onAllResourcesTallied(); // change from resources to stressors
        }
    }

    protected onAllResourcesTallied() {
        Application.instance.voiceover.stopVO();

        if (this.focusedPond === 0) {
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_FOOD_TALLY_COMPLETE], HLF.PlayMode.Append);
        }

        this.stressorButtons.forEach(b=>{
            b.setInteractive(true);
            b.outline.enabled = true;
        });

        this.transitionTimeline?.kill();
        this.transitionTimeline = new TimelineLite();
        this.transitionTimeline.to(this.resourceButtons, 0.5, {
            pixi: {
                alpha: 0.5,
            },
        }).to(this.stressorButtons, 0.5, {
            pixi: {
                alpha: 1,
            },
        }).call(()=>{
            RestStopGame.graphSource = "Problems";
            this.drawFullscreenGraph(this.ponds[this.focusedPond!]);
            if (Settings.closedCaptionsEnabled === false && Settings.voiceoverVolume === 0) {
                this.stressorButtons.forEach(b=>b.pulse());
            }
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_INSTRUCTION_2], {mode: HLF.PlayMode.Append, callback: ()=>{
                if (RestStopGame.firstTime && this.focusedPond === 0 && Settings.audioDescriptionEnabled === false) {
                    this.stressorButtons.forEach(b=>b.pulse());
                    HLF.Application.instance.voiceover.playVO(VO.REST_STOP_PROBLEMS_FACT);
                }
            }});
            if (Settings.audioDescriptionEnabled) {
                const voToPlay = [
                    VO.REST_STOP_AUDIO_DESCRIPTION_7A,
                    this.ponds[this.focusedPond!].nameVO,
                    VO.REST_STOP_AUDIO_DESCRIPTION_7B,
                ];

                let graphTypeVO: string;

                if (RestStopGame.graphType === "bargraph") {
                    graphTypeVO = VO.REST_STOP_BUTTON_BAR_GRAPH;
                }
                else {
                    graphTypeVO = VO.REST_STOP_BUTTON_PICTOGRAPH;
                }
                voToPlay.push(graphTypeVO);
                voToPlay.push(VO.REST_STOP_AUDIO_DESCRIPTION_7C);
                HLF.Application.instance.voiceover.playVO(voToPlay, {mode: HLF.PlayMode.Append, priority: 4, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_7A,
                    args: {
                        "POND": this.ponds[this.focusedPond!].nameVO,
                        "GRAPHTYPE": graphTypeVO,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_7B, // hack to extend the duration of the caption
                        "__": VO.REST_STOP_AUDIO_DESCRIPTION_7C,
                    },
                }});

                HLF.Application.instance.voiceover.playVO([
                    VO.REST_STOP_AUDIO_DESCRIPTION_8A,
                    this.ponds[this.focusedPond!].nameVO,
                    VO.REST_STOP_AUDIO_DESCRIPTION_8B,
                    this.ponds[this.focusedPond!].nameVO,
                    VO.REST_STOP_AUDIO_DESCRIPTION_8C,
                    this.ponds[this.focusedPond!].nameVO
                ], {mode: HLF.PlayMode.Append, priority: 4, caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_8A,
                    args: {
                        "POND": this.ponds[this.focusedPond!].nameVO,
                        "_": VO.REST_STOP_AUDIO_DESCRIPTION_8B, // hack to extend duration of caption
                        "__": VO.REST_STOP_AUDIO_DESCRIPTION_8C,
                        "___": this.ponds[this.focusedPond!].nameVO,
                        "____": this.ponds[this.focusedPond!].nameVO,
                    },
                }});
            }
        });

        if (Settings.audioDescriptionEnabled) {
            PubSub.publishSync(HLF.CLEAR_FOCUS);
        }
    }

    protected onAllPondsTallied() {
        PubSub.publishSync(HLF.CLEAR_FOCUS);
        this.pondPreview1.visible = true;
        this.pondPreview2.visible = true;
        [...this.compareGraphBargraphBg, ...this.compareGraphPictographBg].forEach(sprite=>{
            sprite.interactive = true;
            sprite.buttonMode = true;
        });
        this.compareGraphBargraphBg[0].addListener(HLF.Input.Events.POINTER_TAP, ()=>this.pondPreview1.onFocusActivated());
        this.compareGraphPictographBg[0].addListener(HLF.Input.Events.POINTER_TAP, ()=>this.pondPreview1.onFocusActivated());
        this.compareGraphBargraphBg[1].addListener(HLF.Input.Events.POINTER_TAP, ()=>this.pondPreview2.onFocusActivated());
        this.compareGraphPictographBg[1].addListener(HLF.Input.Events.POINTER_TAP, ()=>this.pondPreview2.onFocusActivated());

        if (RestStopGame.firstTime) {
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_POND_2_COMPLETE], HLF.PlayMode.Append);
        }
        this.transitionTimeline?.kill();
        this.transitionTimeline = new TimelineLite();
        this.transitionTimeline
            .add([
                TweenLite.to(this.pondPreview1, 0.5, {
                    pixi: {
                        scale: 0.5,
                        x: 256,
                        y: 150,
                        alpha: 1,
                    }
                }),
                TweenLite.to(this.pondPreview2, 0.5, {
                    pixi: {
                        scale: 0.5,
                        x: 768,
                        y: 150,
                        alpha: 1,
                    }
                }),
            ])
            .call(()=>{
                this.pondPreview1.interactive = true;
                this.pondPreview2.interactive = true;
                if (RestStopGame.firstTime) {
                    HLF.Application.instance.voiceover.playVO([
                        VO.REST_STOP_ONBOARDING_6A,
                        this.pondPreview1.nameVO,
                        VO.REST_STOP_ONBOARDING_6B,
                        this.pondPreview2.nameVO,
                        VO.REST_STOP_ONBOARDING_6C,
                    ], {
                        mode: HLF.PlayMode.Append,
                        caption: {
                            id: VO.REST_STOP_ONBOARDING_6A,
                            args: {
                                "POND1": this.pondPreview1.nameVO,
                                "POND2": this.pondPreview2.nameVO,
                                "_": VO.REST_STOP_ONBOARDING_6B, // hack to extend duration of caption
                                "__": VO.REST_STOP_ONBOARDING_6C,
                            },
                        },
                        priority: 4,
                        callback: ()=>{
                            this.pondPreview1.pulse();
                            this.pondPreview2.pulse();
                        }
                    });
                }
                else {
                    HLF.Application.instance.voiceover.playVO([VO.REST_STOP_INSTRUCTION_3], HLF.PlayMode.Append);
                }
            });

        this.drawCompareGraphs();

        if (Settings.audioDescriptionEnabled) {
            const voToPlay = [
                VO.REST_STOP_AUDIO_DESCRIPTION_9A,
                this.ponds[0].nameVO,
                VO.REST_STOP_AUDIO_DESCRIPTION_9B,
                this.ponds[1].nameVO,
                VO.REST_STOP_AUDIO_DESCRIPTION_9C,
            ];

            let graphTypeVO: string;
            if (RestStopGame.graphType === "bargraph") {
                graphTypeVO = VO.REST_STOP_BUTTON_BAR_GRAPH;
            }
            else {
                graphTypeVO = VO.REST_STOP_BUTTON_PICTOGRAPH;
            }

            voToPlay.push(graphTypeVO);
            voToPlay.push(VO.REST_STOP_AUDIO_DESCRIPTION_9D);
            HLF.Application.instance.voiceover.playVO(voToPlay, {mode: HLF.PlayMode.Append, priority: 4,
                caption: {
                    id: VO.REST_STOP_AUDIO_DESCRIPTION_9A,
                    args: {
                        "POND1": this.ponds[0].nameVO,
                        "POND2": this.ponds[1].nameVO,
                        "GRAPHTYPE": graphTypeVO,
                    }
                }
            });

            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_AUDIO_DESCRIPTION_IDLE, {mode: HLF.PlayMode.Append, priority: 4});
        }

        const focusables = [
            this.pondPreview1,
            this.pondPreview2,
            this.pictoGraphButton,
            this.barGraphButton,
        ];
        focusables.forEach(button => button.interactive = false); // hack: set interactive to false so keyboard focus doesn't immediately jump to first item
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, focusables);
        focusables.forEach(button => button.interactive = true);
    }

    protected drawPictoGraph(pData: ITallyData, pGraphSource: GraphSource) {

        this.compareGraphContainer.visible = false;

        this.pictoGraphTitleText.setText(pGraphSource);
        this.pictoGraphTitleText.hoverVO = pGraphSource === "Food" ? VO.REST_STOP_FOOD_TITLE_HOVER : VO.REST_STOP_PROBLEMS_TITLE_HOVER;

        const columns = [
            this.pictoGraphColumnOneIconBg,
            this.pictoGraphColumnTwoIconBg,
            this.pictoGraphColumnThreeIconBg,
        ];

        if (pGraphSource === "Problems") {
            for (let x = 0; x < Object.keys(pData.stressors).length; x++) {
                const stressorType = Object.keys(pData.stressors)[x] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                const maxY = pData.stressors[stressorType]!;

                this.pictoGraphXAxisIcon[x].setLayoutTexture(stressorTexture, 0.5, 0.5);
                this.pictoGraphXAxisIcon[x].visible = true;
                this.pictoGraphPlusButton[x].visible = true;
                this.pictoGraphPlusButton[x].hoverVO = templateStressors[stressorType].hoverVO;

                for (let y = 0; y < maxY; y++) {
                    columns[x][y].visible = true;
                    columns[x][y].setIcon(stressorTexture);
                }
                for (let y = maxY; y < columns[x].length; y++) {
                    columns[x][y].visible = false;
                }
            }
            // hide remaining columns if fewer than 3 stressor types
            for (let x = Object.keys(pData.stressors).length; x < 3; x++) {
                this.pictoGraphXAxisIcon[x].visible = false;
                this.pictoGraphPlusButton[x].visible = false;
                for (let y = 0; y < columns[x].length; y++) {
                    columns[x][y].visible = false;
                }
            }
        }
        else {
            for (let x = 0; x < Object.keys(pData.resources).length; x++) {
                const resourceType = Object.keys(pData.resources)[x] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                const maxY = pData.resources[Object.keys(pData.resources)[x] as ResourceType]!;

                this.pictoGraphXAxisIcon[x].setLayoutTexture(resourceTexture, 0.5, 0.5);
                this.pictoGraphXAxisIcon[x].visible = true;
                this.pictoGraphPlusButton[x].visible = true;
                this.pictoGraphPlusButton[x].hoverVO = templateResources[resourceType].hoverVO;

                for (let y = 0; y < maxY; y++) {
                    columns[x][y].visible = true;
                    columns[x][y].setIcon(resourceTexture);
                }
                for (let y = maxY; y < columns[x].length; y++) {
                    columns[x][y].visible = false;
                }
            }
            // hide remaining columns if fewer than 3 resource types
            for (let x = Object.keys(pData.resources).length; x < 3; x++) {
                this.pictoGraphXAxisIcon[x].visible = false;
                this.pictoGraphPlusButton[x].visible = false;
                for (let y = 0; y < columns[x].length; y++) {
                    columns[x][y].visible = false;
                }
            }
        }
    }

    protected drawBarGraph(pData: ITallyData, pGraphSource: GraphSource) {

        this.compareGraphContainer.visible = false;

        const maxStressorVal = Math.max(...Object.keys(pData.stressors).map(k => pData.stressors[k as StressorType]!));
        const maxResourceVal = Math.max(...Object.keys(pData.resources).map(k => pData.resources[k as ResourceType]!));
        const maxY = Math.max(maxStressorVal, maxResourceVal); // TODO: update y axis labels

        this.barGraphTitleText.setText(pGraphSource);
        this.barGraphTitleText.hoverVO = pGraphSource === "Food" ? VO.REST_STOP_FOOD_TITLE_HOVER : VO.REST_STOP_PROBLEMS_TITLE_HOVER;

        if (pGraphSource === "Problems") {
            this.stressorsBarGraphBar.forEach(bar=>bar.visible = true);
            this.resourcesBarGraphBar.forEach(bar=>bar.visible = false);
            for (let x = 0; x < Object.keys(pData.stressors).length; x++) {
                const stressorType = Object.keys(pData.stressors)[x] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                this.barGraphXAxisIcon[x].setLayoutTexture(stressorTexture, 0.5, 0.5);
                this.barGraphXAxisIcon[x].visible = true;
                this.barGraphPlusButton[x].visible = true;
                this.barGraphPlusButton[x].hoverVO = templateStressors[stressorType].hoverVO;

                const y = (pData.stressors[Object.keys(pData.stressors)[x] as StressorType]!);
                this.stressorsBarGraphBar[x].setNineSliceScale(1, y);
                this.stressorsBarGraphBar[x].visible = true;
            }
            // hide remaining columns if fewer than 3 stressor types
            for (let x = Object.keys(pData.stressors).length; x < 3; x++) {
                this.barGraphXAxisIcon[x].visible = false;
                this.barGraphPlusButton[x].visible = false;
                this.stressorsBarGraphBar[x].visible = false;
            }
        }
        else {
            this.resourcesBarGraphBar.forEach(bar=>bar.visible = true);
            this.stressorsBarGraphBar.forEach(bar=>bar.visible = false);

            for (let x = 0; x < Object.keys(pData.resources).length; x++) {
                const resourceType = Object.keys(pData.resources)[x] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                this.barGraphXAxisIcon[x].setLayoutTexture(resourceTexture, 0.5, 0.5);
                this.barGraphXAxisIcon[x].visible = true;
                this.barGraphPlusButton[x].visible = true;
                this.barGraphPlusButton[x].hoverVO = templateResources[resourceType].hoverVO;

                const y = (pData.resources[Object.keys(pData.resources)[x] as ResourceType]!);
                this.resourcesBarGraphBar[x].setNineSliceScale(1, y);
                this.resourcesBarGraphBar[x].visible = true;
            }
            // hide remaining columns if fewer than 3 resource types
            for (let x = Object.keys(pData.resources).length; x < 3; x++) {
                this.barGraphXAxisIcon[x].visible = false;
                this.barGraphPlusButton[x].visible = false;
                this.resourcesBarGraphBar[x].visible = false;
            }
        }
    }

    protected onPictoGraphButtonClicked() {
        RestStopGame.graphType = "pictograph";
        this.barGraphButton.setInteractive(true);
        this.pictoGraphButton.setInteractive(false);
        this.pictoGraphContainer.visible = true;
        this.barGraphContainer.visible = false;

        if (this.focusedPond !== undefined) {
            this.drawFullscreenGraph(this.ponds[this.focusedPond]);
            this.checkIfAllItemsTallied();
        }
        else {
            this.drawCompareGraphs();
        }

        if (RestStopGame.firstTime === true && this.focusedPond === 1 && this.didClickPictographButton === false) {
            this.pictoGraphButton.stopPulse();
            HLF.Application.instance.voiceover.playVO(VO.REST_STOP_ONBOARDING_5);
        }
        this.didClickPictographButton = true;
        this.restartIdleTimer();
        Application.instance.analytics.event("select_content", "Pictograph");
    }

    protected onBarGraphButtonClicked() {
        RestStopGame.graphType = "bargraph";
        this.pictoGraphButton.setInteractive(true);
        this.barGraphButton.setInteractive(false);
        this.barGraphContainer.visible = true;
        this.pictoGraphContainer.visible = false;

        if (this.focusedPond !== undefined) {
            this.drawFullscreenGraph(this.ponds[this.focusedPond]);
            this.checkIfAllItemsTallied();
        }
        else {
            this.drawCompareGraphs();
        }
        this.restartIdleTimer();
        Application.instance.analytics.event("select_content", "Bar Graph");
    }
    private onPondFocused(pPond: number) {
        let delay = 0;

        if (Settings.closedCaptionsEnabled === false && Settings.voiceoverVolume === 0) {
            this.resourceButtons.forEach(b=>b.pulse());
        }
        else if (RestStopGame.firstTime && pPond === 0 && !GameState.freePlay) { // HACK: wait for onboarding vo to complete
            delay = 6;
        }
        TweenLite.delayedCall(delay, ()=>{
            HLF.Application.instance.voiceover.playVO([VO.REST_STOP_INSTRUCTION_1A, this.ponds[pPond].nameVO, VO.REST_STOP_INSTRUCTION_1B], {
                caption: {
                    id: VO.REST_STOP_INSTRUCTION_1A,
                    args: {
                        "POND": this.ponds[pPond].nameVO,
                        "_": VO.REST_STOP_INSTRUCTION_1B, // hack to make caption duration longer
                    },
                },
                mode: HLF.PlayMode.Append,
                callback:()=>{
                    if (RestStopGame.firstTime && pPond === 0) {
                        this.resourceButtons.forEach(b=>b.pulse());
                        HLF.Application.instance.voiceover.playVO([VO.REST_STOP_ONBOARDING_2, VO.REST_STOP_ONBOARDING_3]);
                    }
                    else if (RestStopGame.firstTime && pPond === 1 && this.didClickPictographButton === false) {
                        this.pictoGraphButton.pulse();
                        HLF.Application.instance.voiceover.playVO([VO.REST_STOP_ONBOARDING_4]);
                    }
                },
            });
        });
    }

    private drawFullscreenGraph(pPond: RestStopPond) {
        if (RestStopGame.graphType === "bargraph") {
            this.drawBarGraph(pPond.tally, RestStopGame.graphSource);
        }
        else {
            this.drawPictoGraph(pPond.tally, RestStopGame.graphSource);
        }
    }

    private drawCompareGraphs() {

        this.pictoGraphContainer.visible = false;
        this.barGraphContainer.visible = false;
        this.compareGraphContainer.visible = true;

        if (RestStopGame.graphType === "bargraph") {
            this.compareGraphBarGraphContainer.visible = true;
            this.compareGraphPictoGraphContainer.visible = false;

            this.compareGraphBarGraphStressorsXAxisIcon.forEach(icon=>icon.visible = false);
            this.compareGraphBarGraphStressorBar.forEach(bar=>bar.visible = false);
            this.compareGraphBarGraphResourcesXAxisIcon.forEach(icon=>icon.visible = false);
            this.compareGraphBarGraphResourceBar.forEach(bar=>bar.visible = false);

            // pond 1 stressors
            for (let i = 0; i < Object.keys(this.pondPreview1.tally.stressors).length; i++) {
                const stressorType = Object.keys(this.pondPreview1.tally.stressors)[i] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                const x = Object.keys(this.pondPreview1.tally.stressors).indexOf(stressorType);

                this.compareGraphBarGraphStressorsXAxisIcon[x].setLayoutTexture(stressorTexture, 0.5, 0.5);
                this.compareGraphBarGraphStressorsXAxisIcon[x].visible = true;

                const y = this.pondPreview1.tally.stressors[stressorType]!;
                this.compareGraphBarGraphStressorBar[x].setNineSliceScale(1, y);
                this.compareGraphBarGraphStressorBar[x].visible = true;
            }

            // pond 2 stressors
            for (let i = 0; i < Object.keys(this.pondPreview2.tally.stressors).length; i++) {
                const stressorType = Object.keys(this.pondPreview2.tally.stressors)[i] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                const x = Object.keys(this.pondPreview2.tally.stressors).indexOf(stressorType);

                this.compareGraphBarGraphStressorsXAxisIcon[x + 3].setLayoutTexture(stressorTexture, 0.5, 0.5);
                this.compareGraphBarGraphStressorsXAxisIcon[x + 3].visible = true;

                const y = this.pondPreview2.tally.stressors[stressorType]!;
                this.compareGraphBarGraphStressorBar[x + 3].setNineSliceScale(1, y);
                this.compareGraphBarGraphStressorBar[x + 3].visible = true;
            }

            // pond 1 resources
            for (let i = 0; i < Object.keys(this.pondPreview1.tally.resources).length; i++) {
                const resourceType = Object.keys(this.pondPreview1.tally.resources)[i] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                const x = Object.keys(this.pondPreview1.tally.resources).indexOf(resourceType);

                this.compareGraphBarGraphResourcesXAxisIcon[x].setLayoutTexture(resourceTexture, 0.5, 0.5);
                this.compareGraphBarGraphResourcesXAxisIcon[x].visible = true;

                const y = this.pondPreview1.tally.resources[resourceType]!;
                this.compareGraphBarGraphResourceBar[x].setNineSliceScale(1, y);
                this.compareGraphBarGraphResourceBar[x].visible = true;
            }

            // pond 2 resources
            for (let i = 0; i < Object.keys(this.pondPreview2.tally.resources).length; i++) {
                const resourceType = Object.keys(this.pondPreview2.tally.resources)[i] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                const x = Object.keys(this.pondPreview2.tally.resources).indexOf(resourceType);

                this.compareGraphBarGraphResourcesXAxisIcon[x + 3].setLayoutTexture(resourceTexture, 0.5, 0.5);
                this.compareGraphBarGraphResourcesXAxisIcon[x + 3].visible = true;

                const y = this.pondPreview2.tally.resources[resourceType]!;
                this.compareGraphBarGraphResourceBar[x + 3].setNineSliceScale(1, y);
                this.compareGraphBarGraphResourceBar[x + 3].visible = true;
            }

        }
        else {
            this.compareGraphPictoGraphContainer.visible = true;
            this.compareGraphBarGraphContainer.visible = false;

            const stressorColumns = [
                this.compareGraphPictoGraphPondOneStressorsBarOneIconBg,
                this.compareGraphPictoGraphPondOneStressorsBarTwoIconBg,
                this.compareGraphPictoGraphPondOneStressorsBarThreeIconBg,

                this.compareGraphPictoGraphPondTwoStressorsBarOneIconBg,
                this.compareGraphPictoGraphPondTwoStressorsBarTwoIconBg,
                this.compareGraphPictoGraphPondTwoStressorsBarThreeIconBg,
            ];

            const resourceColumns = [
                this.compareGraphPictoGraphPondOneResourcesBarOneIconBg,
                this.compareGraphPictoGraphPondOneResourcesBarTwoIconBg,
                this.compareGraphPictoGraphPondOneResourcesBarThreeIconBg,

                this.compareGraphPictoGraphPondTwoResourcesBarOneIconBg,
                this.compareGraphPictoGraphPondTwoResourcesBarTwoIconBg,
                this.compareGraphPictoGraphPondTwoResourcesBarThreeIconBg,
            ];

            stressorColumns.forEach(col=>col.forEach(icon=>icon.visible=false));
            resourceColumns.forEach(col=>col.forEach(icon=>icon.visible=false));

            this.compareGraphPictoGraphStressorsXAxisIconBg.forEach(icon=>icon.visible = false);
            this.compareGraphPictoGraphResourcesXAxisIconBg.forEach(icon=>icon.visible = false);

            // pond 1 stressors
            for (let i = 0; i < Object.keys(this.pondPreview1.tally.stressors).length; i++) {
                const stressorType = Object.keys(this.pondPreview1.tally.stressors)[i] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                const x = Object.keys(this.pondPreview1.tally.stressors).indexOf(stressorType);
                this.compareGraphPictoGraphStressorsXAxisIconBg[x].setIcon(stressorTexture);
                this.compareGraphPictoGraphStressorsXAxisIconBg[x].visible = true;
                const maxY = this.pondPreview1.tally.stressors[stressorType]!;
                for (let y = 0; y < maxY; y++) {
                    stressorColumns[x][y].visible = true;
                    stressorColumns[x][y].setIcon(stressorTexture);
                }
                for (let y = maxY; y < stressorColumns[x].length; y++) {
                    stressorColumns[x][y].visible = false;
                }
            }

            // pond 2 stressors
            for (let i = 0; i < Object.keys(this.pondPreview2.tally.stressors).length; i++) {
                const stressorType = Object.keys(this.pondPreview2.tally.stressors)[i] as StressorType;
                const stressorTexture = HLF.PixiUtils.getTexture(templateStressors[stressorType].sprite_graph);
                const x = Object.keys(this.pondPreview2.tally.stressors).indexOf(stressorType);
                this.compareGraphPictoGraphStressorsXAxisIconBg[x + 3].setIcon(stressorTexture);
                this.compareGraphPictoGraphStressorsXAxisIconBg[x + 3].visible = true;
                const maxY = this.pondPreview2.tally.stressors[stressorType]!;
                for (let y = 0; y < maxY; y++) {
                    stressorColumns[x + 3][y].visible = true;
                    stressorColumns[x + 3][y].setIcon(stressorTexture);
                }
                for (let y = maxY; y < stressorColumns[x + 3].length; y++) {
                    stressorColumns[x + 3][y].visible = false;
                }
            }

            // pond 1 resources
            for (let i = 0; i < Object.keys(this.pondPreview1.tally.resources).length; i++) {
                const resourceType = Object.keys(this.pondPreview1.tally.resources)[i] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                const x = Object.keys(this.pondPreview1.tally.resources).indexOf(resourceType);
                this.compareGraphPictoGraphResourcesXAxisIconBg[x].setIcon(resourceTexture);
                this.compareGraphPictoGraphResourcesXAxisIconBg[x].visible = true;
                const maxY = this.pondPreview1.tally.resources[resourceType]!;
                for (let y = 0; y < maxY; y++) {
                    resourceColumns[x][y].visible = true;
                    resourceColumns[x][y].setIcon(resourceTexture);
                }
                for (let y = maxY; y < stressorColumns[x].length; y++) {
                    resourceColumns[x][y].visible = false;
                }
            }

            // pond 2 resources
            for (let i = 0; i < Object.keys(this.pondPreview2.tally.resources).length; i++) {
                const resourceType = Object.keys(this.pondPreview2.tally.resources)[i] as ResourceType;
                const resourceTexture = HLF.PixiUtils.getTexture(templateResources[resourceType].sprite_graph);
                const x = Object.keys(this.pondPreview2.tally.resources).indexOf(resourceType);
                this.compareGraphPictoGraphResourcesXAxisIconBg[x + 3].setIcon(resourceTexture);
                this.compareGraphPictoGraphResourcesXAxisIconBg[x + 3].visible = true;
                const maxY = this.pondPreview2.tally.resources[resourceType]!;
                for (let y = 0; y < maxY; y++) {
                    resourceColumns[x + 3][y].visible = true;
                    resourceColumns[x + 3][y].setIcon(resourceTexture);
                }
                for (let y = maxY; y < stressorColumns[x + 3].length; y++) {
                    resourceColumns[x + 3][y].visible = false;
                }
            }
        }
    }


    private initPonds() {
        let pondBg1: keyof typeof templatePonds;
        let pondBg2: keyof typeof templatePonds;

        if (Settings.difficulty === 3 / 3) {
            pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds) as (keyof typeof templatePonds)[]);
            pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
        }
        else if (Settings.difficulty === 2 / 3) {
            pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>!k.includes("Lrg")) as (keyof typeof templatePonds)[]);
            pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>!k.includes("Lrg")).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
        }
        else if (Settings.difficulty === 1 / 3) {
            pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>k.includes("Sm")) as (keyof typeof templatePonds)[]);
            pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>k.includes("Sm")).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
        }
        else { // adaptive difficulty
            switch (this.ducks.length) {
                default:
                case 5:
                    pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>k.includes("Sm")) as (keyof typeof templatePonds)[]);
                    pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>k.includes("Sm")).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
                    break;
                case 7:
                    pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>!k.includes("Lrg")) as (keyof typeof templatePonds)[]);
                    pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(k=>!k.includes("Lrg")).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
                    break;
                case 9:
                    pondBg1 = HLF.ArrayUtils.random(Object.keys(templatePonds) as (keyof typeof templatePonds)[]);
                    pondBg2 = HLF.ArrayUtils.random(Object.keys(templatePonds).filter(p=>p!==pondBg1) as (keyof typeof templatePonds)[]);
                    break;
            }
        }

        this.pondPreview1.setLayoutTexture(pondBg1, 0.5, 0.5);
        this.pondPreview2.setLayoutTexture(pondBg2, 0.5, 0.5);

        this.pondPreview1.position.set(512 - this.pondPreview1.width / 2 - 60, 384); // left of center
        this.pondPreview2.position.set(512 + this.pondPreview2.width / 2 + 60, 384); // right of center

        this.pondPreview1.interactive = false;
        this.pondPreview2.interactive = false;

        this.pondPreview1.name = templatePonds[pondBg1].name;
        this.pondPreview1.hoverVO = templatePonds[pondBg1].nameVO;
        this.pondPreview2.name = templatePonds[pondBg2].name;
        this.pondPreview2.hoverVO = templatePonds[pondBg2].nameVO;

        this.comparePondTitleText1 = new Text(this.pondPreview1.name, Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: true});
        this.comparePondTitleBg[0].addChild(this.comparePondTitleText1);
        this.comparePondTitleBg[0].setNineSliceScale(this.comparePondTitleText1.textWidth / TEXT_BG_WIDTH, 0.6);
        this.comparePondTitleBg[0].scale.set(Settings.textSizeMultiplier);

        this.comparePondTitleText2 = new Text(this.pondPreview2.name, Constants.FONT_RIFFICFREE_BOLD_36, 36, new PIXI.Point(0.5, 0.5), undefined, undefined, {dontScale: true});
        this.comparePondTitleBg[1].addChild(this.comparePondTitleText2);
        this.comparePondTitleBg[1].setNineSliceScale(this.comparePondTitleText2.textWidth / TEXT_BG_WIDTH, 0.6);
        this.comparePondTitleBg[1].scale.set(Settings.textSizeMultiplier);

        let ok = this.generateTwoPonds(pondBg1, pondBg2);
        let i = 1;
        while (ok === false) { // yes this is potentially an infinite loop
            i++;
            ok = this.generateTwoPonds(pondBg1, pondBg2);
        }
        const betterPond = pondData[0].resources.length > pondData[1].resources.length ? 0 : 1;
        console.log("after " + i + " attempts to generate random ponds:");
        console.log("pond " + (betterPond + 1) + " is better, because it has " + pondData[betterPond].resources.length + " food and " + pondData[betterPond].stressors.length + " problems");
        console.log("pond " + ((1-betterPond) + 1) + " is worse; it has " + pondData[1-betterPond].resources.length + " food and " + pondData[1-betterPond].stressors.length + " problems");
    }


    private generateTwoPonds(pondBg1: keyof typeof templatePonds, pondBg2: keyof typeof templatePonds) {
        pondData[0] = RestStopGame.GenerateRandomPond(templatePonds[pondBg1].squares);
        pondData[1] = RestStopGame.GenerateRandomPond(templatePonds[pondBg2].squares);

        const pondsHaveSameNumberOfFood = pondData[0].resources.length === pondData[1].resources.length;

        if (pondsHaveSameNumberOfFood) {
            const pondToModify = HLF.Random.bool() ? 0 : 1;
            if (pondData[pondToModify].resources.length > 1) {
                pondData[pondToModify].resources.pop();
            }
            else if (pondData[1 - pondToModify].resources.length > 1) {
                pondData[1 - pondToModify].resources.pop();
            }
            else {
                console.error("both ponds have exactly 1 food!");
                return false;
            }
        }

        const betterPond = pondData[0].resources.length > pondData[1].resources.length ? 0 : 1;

        const betterPondHasFewerProblems = pondData[betterPond].stressors.length < pondData[1 - betterPond].stressors.length;
        if (betterPondHasFewerProblems === false) {
            const desiredProblemCount = pondData[1 - betterPond].stressors.length - 1;
            if (desiredProblemCount > 0) {
                while (pondData[betterPond].stressors.length > desiredProblemCount && pondData[betterPond].stressors.length > 1) {
                    pondData[betterPond].stressors.pop();
                }
            }
        }

        if (pondData[0].resources.length === 0) {
            console.error("pond 1 has zero food!");
            return false;
        }
        if (pondData[1].resources.length === 0) {
            console.error("pond 2 has zero food!");
            return false;
        }
        if (pondData[0].stressors.length === 0) {
            console.error("pond 1 has zero problems!");
            return false;
        }
        if (pondData[1].stressors.length === 0) {
            console.error("pond 2 has zero problems!");
            return false;
        }

        const betterPondHasEnoughFoodForAllDucks = pondData[betterPond].resources.length >= this.ducks.length;
        if (betterPondHasEnoughFoodForAllDucks === false) {
            // neither pond has enough food for all ducks
            return false;
        }

        const worsePondHasNotEnoughFoodForAllDucks = pondData[1 - betterPond].resources.length < this.ducks.length;
        if (worsePondHasNotEnoughFoodForAllDucks === false) {
            // worse pond has enough food for all ducks
            return false;
        }

        return true;
    }

    private _debugAutoCompleteTally() {
        this.restartIdleTimer();
        if (this.focusedPond !== undefined) {
            this.resourceButtons.forEach((r) => r.onFocusActivated());
            this.stressorButtons.forEach((s) => s.onFocusActivated());
        }
    }

    private restartIdleTimer() {
        if (this.idleTimeline === undefined) {
            console.log("start idle timer");
            this.idleTimeline = new TimelineMax({repeat: -1});
            this.idleTimeline.call(()=>{
                if (this.focusedPond !== undefined) {
                    console.log("idle -- tallying");
                    HLF.Application.instance.voiceover.playVO(VO.REST_STOP_HAVEMORE, {mode: HLF.PlayMode.New, callback: ()=>this.restartIdleTimer()});
                }
                else if (Settings.audioDescriptionEnabled) {
                    console.log("idle -- compare + audio descriptions");
                    HLF.Application.instance.voiceover.playVO(VO.REST_STOP_AUDIO_DESCRIPTION_IDLE, {mode: HLF.PlayMode.New, callback: ()=>this.restartIdleTimer()});
                }
                else {
                    console.log("idle -- compare");
                    // todo: random exhaust instead of random
                    HLF.Application.instance.voiceover.playVO(HLF.ArrayUtils.random([
                        VO.REST_STOP_COMPARE_IDLE_1,
                        VO.REST_STOP_COMPARE_IDLE_2,
                        VO.REST_STOP_COMPARE_IDLE_3,
                        VO.REST_STOP_COMPARE_IDLE_4,
                    ]), {mode: HLF.PlayMode.New, callback: ()=>this.restartIdleTimer()});
                }
            }, undefined, undefined, 15);
        }
        else {
            this.idleTimeline?.kill();
            this.idleTimeline?.restart();
            console.log("restart idle timer");
        }
    }

    private stopIdleTimer() {
        if (this.idleTimeline !== undefined) {
            console.log("stop idle timer");
            this.idleTimeline.kill();
        }
    }
}
